import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { Decorator } from "final-form";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-calculate";
import { pick, uniq } from "lodash";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  IWidget,
  IWizard,
  IWizardSaveRequest,
  WizardWidget,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { globalContextUpdaters } from "utils/formUtils";
import OrderableWidgets from "./OrderableWidgets";
import SelectInflectionPoint from "./SelectInflectionPoint";
import WidgetSearch from "./WidgetSearch";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    alignItems: "center",
  },
});

type WizardFormValues = Omit<Partial<IWizard>, "organizationName">;

const { Form } = withTypes<WizardFormValues>();

type WizardFormProps = {
  wizard?: IWizard;
  submitLabel: string;
  organizationPickerMode?: ContextOrganizationFieldMode
  onSubmit: (
    values: IWizardSaveRequest
  ) => object | Promise<object | void> | void;
  onCancel?: () => void;
};

const WizardForm = ({
  wizard,
  submitLabel,
  organizationPickerMode,
  onCancel,
  onSubmit,
}: WizardFormProps & Omit<FormProps<WizardFormValues>, "onSubmit">) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { organizationId } = contextStore.useState();

  const init = useMemo<WizardFormValues>(
    () => ({
      organizationId,
      widgets: [],
      ...pick(wizard ?? {}, [
        "organizationId",
        "inflectionPoint",
        "name",
        "header",
        "footer",
        "isActive",
        "widgets",
      ]),
    }),
    [organizationId, wizard]
  );

  const calculator = useMemo(() => {
    return createDecorator(
      ...globalContextUpdaters({})
    ) as Decorator<WizardFormValues>;
  }, []);

  const validateForm = (values: WizardFormValues) => {
    if (!values.widgets?.length) {
      return { widgets: t({ id: "requiredLabel" }) };
    } else if (
      values.widgets.length !== uniq(values.widgets.map((w) => w.id)).length
    ) {
      return { widgets: t({ id: "widgetCannotBeAddedTwice" }) };
    }
  };

  return (
    <Form
      onSubmit={async (values) => {
        const transformed = {
          id: values.id,
          inflectionPointId: values.inflectionPoint!.id,
          organizationId: values.organizationId,
          isActive: values.isActive ?? false,
          name: values.name as string,
          header: values.header as string,
          footer: values.footer as string,
          widgets: values.widgets!.map((widget, sequence) =>
            WizardWidget.fromJS({
              widgetId: widget.id,
              sequence: sequence + 1,
            })
          ),
        };
        return await onSubmit(transformed);
      }}
      initialValues={init}
      decorators={[calculator]}
      mutators={{ ...arrayMutators }}
      validate={validateForm}
      render={({
        form: {
          mutators: { push },
          change,
        },
        handleSubmit,
        submitting,
        submitError,
        values,
      }) => {
        const onAddWidget = (widget: IWidget) => {
          push("widgets", widget);
        };
        return (
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={change}
                  mode={organizationPickerMode}
                />

                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="nameText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="isActive" type="checkbox">
                  {({ input }) => (
                    <Grid item xs={6}>
                      <Box sx={styles.lineContainer}>
                        <FormLabel>
                          <T id="isActiveLabel" />
                        </FormLabel>
                        <Checkbox {...input} data-cy="isActiveCheckBox" />
                      </Box>
                    </Grid>
                  )}
                </Field>

                <Field name="inflectionPoint" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectInflectionPoint
                          required={true}
                          {...input}
                          label={t({
                            id: "inflectionPointLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="header" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        multiline
                        rows={3}
                        label={t({ id: "headerTextLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="headerText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="footer" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        multiline
                        rows={3}
                        label={t({ id: "footerTextLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="footerText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Grid item xs={12}>
                  <Box mt={3} />
                  <FieldArray name="widgets" nonZeroArray>
                    {({ fields, meta }) => (
                      <FormControl fullWidth>
                        <FormLabel
                          error={meta.error && meta.submitFailed}
                          required
                        >
                          <T id="widgetsLabel" />
                        </FormLabel>
                        <Box mt={3} />
                        <OrderableWidgets fields={fields} />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText error>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12}>
                  <WidgetSearch
                    onAddWidget={onAddWidget}
                    selectedWidgetIds={
                      values.widgets?.map((widget) => widget.id) ?? []
                    }
                  />
                </Grid>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="submitButton"
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        );
      }}
    />
  );
};

export default WizardForm;
